import React, { useState, useEffect } from 'react';
import { UserManagementAPI, OrganizationsAPI } from '../../Services/PharmacyAPI';
import DataTable from 'react-data-table-component';
import SpinnerModal from '../../ShareComponent/Spinner/SpinnerModal';
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import CommonModal from '../Common/CommonModal';
import './UserManagement.css';
import '../Common/common.css';
import { getOrganizationId } from '../../Utils/jwtUtils';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Get Organization ID from JWT token
  const organizationId = getOrganizationId() || 1;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setIsLoading(true);
      
      const [usersData, rolesData, organizationsData] = await Promise.all([
        UserManagementAPI.getAllUsers(organizationId),
        UserManagementAPI.getRoles(),
        OrganizationsAPI.getAllOrganizations()
      ]);

      setUsers(usersData || []);
      setRoles(rolesData || []);
      setOrganizations(organizationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
      setUsers([]);
      setRoles([]);
      setOrganizations([]);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(user => 
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.isActive : !user.isActive
      );
    }

    setFilteredUsers(filtered);
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedUser(null);
    resetFormData();
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      address: user.address || '',
      organizationID: user.organizationID,
      role: user.role,
      department: user.department || '',
      licenseNumber: user.licenseNumber || '',
      isActive: user.isActive,
      isEmailVerified: user.isEmailVerified
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.firstName} ${user.lastName}?`)) {
      return;
    }

    try {
      setIsLoading(true);
      await UserManagementAPI.deleteUser(user.userID);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleSavePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setIsLoading(true);
      await UserManagementAPI.changePassword(selectedUser.userID, passwordData.newPassword);
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      if (modalMode === 'create') {
        const userData = {
          ...formData,
          organizationID: organizationId
        };
        await UserManagementAPI.createUser(userData);
        toast.success('User created successfully');
      } else {
        const userData = {
          ...formData,
          lastUpdatedBy: 'Admin' // In real app, get from auth context
        };
        await UserManagementAPI.updateUser(selectedUser.userID, userData);
        toast.success('User updated successfully');
      }
      
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save user';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      address: '',
      organizationID: organizationId, // Default to current user's organization
      role: 'User',
      department: '',
      licenseNumber: '',
      isActive: true,
      isEmailVerified: false
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY');
  };

  const columns = [
    {
      name: 'User',
      selector: (row) => `${row.firstName} ${row.lastName}`,
      sortable: true,
      cell: (row) => (
        <div className="user-info">
          <div className="user-name">{row.firstName} {row.lastName}</div>
          <div className="user-email">{row.email}</div>
        </div>
      ),
    },
    {
      name: 'Contact',
      selector: (row) => row.phone || 'N/A',
      sortable: true,
      cell: (row) => (
        <div className="contact-info">
          {row.phone && <div className="contact-phone">{row.phone}</div>}
          {row.address && (
            <div className="contact-address">{row.address}</div>
          )}
        </div>
      ),
    },
    {
      name: 'Role',
      selector: (row) => row.role,
      sortable: true,
      cell: (row) => (
        <span className={`role-badge ${row.role.toLowerCase()}`}>
          {row.role}
        </span>
      ),
    },
    {
      name: 'Department',
      selector: (row) => row.department || 'N/A',
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => row.isActive,
      sortable: true,
      cell: (row) => (
        <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      name: 'Last Login',
      selector: (row) => row.lastLoginDate,
      sortable: true,
      cell: (row) => formatDate(row.lastLoginDate),
    },
    {
      name: 'Created Date',
      selector: (row) => row.createdDate,
      sortable: true,
      cell: (row) => formatDate(row.createdDate),
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn edit"
            onClick={() => handleEdit(row)}
            title="Edit User"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className="action-btn password"
            onClick={() => handleChangePassword(row)}
            title="Change Password"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 2L19 4M7 20L5 22M1 12C1 16.9706 5.02944 21 10 21C12.5 21 14.7 20 16.5 18.5M23 12C23 7.02944 18.9706 3 14 3C11.5 3 9.3 4 7.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className="action-btn delete"
            onClick={() => handleDelete(row)}
            title="Delete User"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 7L5 7M10 11V17M14 11V17M8 7V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V7M4 7H20L19 19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19L4 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const customStyles = {
    table: {
      style: {
        border: "none",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
      },
    },
    headRow: {
      style: {
        backgroundColor: "var(--bg-tertiary)",
        borderBottom: "1px solid var(--border-color)",
      },
    },
    headCells: {
      style: {
        fontSize: "0.875rem",
        fontWeight: "600",
        color: "var(--text-primary)",
        padding: "1rem 1.5rem",
        textTransform: "uppercase",
        letterSpacing: "0.025em",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid var(--border-light)",
        "&:hover": {
          backgroundColor: "var(--bg-secondary)",
        },
      },
    },
    cells: {
      style: {
        fontSize: "0.875rem",
        color: "var(--text-primary)",
        padding: "1rem 1.5rem",
      },
    },
    pagination: {
      style: {
        backgroundColor: "var(--bg-card)",
        borderTop: "1px solid var(--border-light)",
        padding: "1rem 1.5rem",
      },
    },
  };

  return (
    <>
      {isLoading && <SpinnerModal isLoading={true} />}
      <div className="user-management-container">
        <div className="user-management-header">
          <div className="header-content">
            <h1 className="page-title">User Management</h1>
            <p className="page-subtitle">Manage system users and their access permissions</p>
          </div>
          <div className="header-actions">
            <button className="common-button secondary" onClick={loadData}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 4V10H7M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
            <button className="common-button primary" onClick={handleCreate}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Add User
            </button>
          </div>
        </div>

        <div className="user-management-content">
          <div className="filters-card">
            <div className="filters-header">
              <h3 className="filters-title">Filters</h3>
              <button 
                className="clear-filters"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                }}
              >
                Clear All
              </button>
            </div>
            <div className="filters-grid">
              <div className="filter-group">
                <label className="common-Labels">Search Users</label>
                <input
                  className="common-Input"
                  type="text"
                  placeholder="Search by name, email, or phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="common-Labels">Role</label>
                <select 
                  className="common-Input"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All Roles</option>
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="common-Labels">Status</label>
                <select 
                  className="common-Input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="table-card">
            <div className="table-header">
              <div className="table-title-section">
                <h3 className="table-title">Users List</h3>
                <p className="table-subtitle">{filteredUsers.length} users found</p>
              </div>
            </div>
            
            <div className="table-container">
              {filteredUsers.length === 0 && !loading ? (
                <div className="no-data-state">
                  <div className="no-data-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>No Users Found</h3>
                  <p>No users match your current filters. Try adjusting your search criteria or add your first user.</p>
                  <button 
                    className="common-button primary"
                    onClick={() => {
                      setSearchTerm('');
                      setRoleFilter('');
                      setStatusFilter('');
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <DataTable
                  title=""
                  columns={columns}
                  data={filteredUsers}
                  progressPending={loading}
                  pagination
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      <CommonModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Add New User' : 'Edit User'}
        onSave={handleSave}
        onCancel={() => setShowModal(false)}
      >
        <div className="modal-form">
          <div className="form-group">
            <label className="common-Labels">First Name <span className="common-Asterisk">*</span></label>
            <input
              className="common-Input"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">Last Name <span className="common-Asterisk">*</span></label>
            <input
              className="common-Input"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">Email <span className="common-Asterisk">*</span></label>
            <input
              className="common-Input"
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          {modalMode === 'create' && (
            <div className="form-group">
              <label className="common-Labels">Password <span className="common-Asterisk">*</span></label>
              <input
                className="common-Input"
                type="password"
                value={formData.password || ''}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
          )}
          <div className="form-group">
            <label className="common-Labels">Organization <span className="common-Asterisk">*</span></label>
            <select
              className="common-Input"
              value={formData.organizationID || organizationId}
              onChange={(e) => setFormData({ ...formData, organizationID: parseInt(e.target.value) })}
              required
            >
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.organizationID} value={org.organizationID}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="common-Labels">Phone</label>
            <input
              className="common-Input"
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">Address</label>
            <textarea
              className="common-Input"
              rows="3"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">Role <span className="common-Asterisk">*</span></label>
            <select
              className="common-Input"
              value={formData.role || 'User'}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="common-Labels">Department</label>
            <input
              className="common-Input"
              type="text"
              value={formData.department || ''}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">License Number</label>
            <input
              className="common-Input"
              type="text"
              value={formData.licenseNumber || ''}
              onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">
              <input
                type="checkbox"
                checked={formData.isActive !== false}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="form-group">
            <label className="common-Labels">
              <input
                type="checkbox"
                checked={formData.isEmailVerified === true}
                onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
              />
              Email Verified
            </label>
          </div>
        </div>
      </CommonModal>

      {/* Change Password Modal */}
      <CommonModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        title={`Change Password - ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        onSave={handleSavePassword}
        onCancel={() => setShowPasswordModal(false)}
      >
        <div className="modal-form">
          <div className="form-group">
            <label className="common-Labels">New Password <span className="common-Asterisk">*</span></label>
            <input
              className="common-Input"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label className="common-Labels">Confirm Password <span className="common-Asterisk">*</span></label>
            <input
              className="common-Input"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              required
              minLength={6}
            />
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default UserManagement;

